import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const WEBRTC_SOURCE = 'https://www.w3.org/TR/webrtc/#rtcicecandidate-interface';
const ICE_SOURCE = 'https://datatracker.ietf.org/doc/html/rfc8445';

/**
 * WebRTC / ICE candidate analysis (fingerprinting-techniques §6).
 *
 * We do not observe the STUN Binding Request itself (that needs a STUN server
 * we operate, and even then it is a passive-observation technique), but the
 * page CAN see what ICE gathering *produces*:
 *  - the candidate gathering order and types
 *  - whether mDNS-obfuscated (*.local) host candidates appear — Chrome's
 *    default since ~M72; their ABSENCE from something claiming to be Chrome is
 *    a strong signal
 *  - candidate priority values and the local/srflx/relay mix
 *
 * Public STUN servers are used by default (see PUBLIC_STUN below). That is what
 * makes `hasSrflx` meaningful: without a STUN server, gathering only ever
 * produces host candidates and the srflx branch is dead. It also exposes the
 * page's own public IP as the reflexive address, which we cross-check across
 * several independent servers.
 *
 * We create a data channel purely to trigger gathering.
 */
export const webrtcProbe: Probe = {
  id: 'webrtc',
  label: 'WebRTC / ICE',
  layer: 'webrtc',
  tier: 'client',
  category: 'hardware',
  description: 'ICE candidate gathering order, mDNS obfuscation, and types',
  budgetMs: 5000,

  async run(): Promise<ProbeResult> {
    if (typeof RTCPeerConnection !== 'function') {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'RTCPeerConnection unavailable'),
        },
      };
    }

    const findings: Finding[] = [];
    const candidates: Array<{ type: string; protocol: string; address: string; port: number; priority: number; isMdns: boolean }> = [];

    const gathered = await new Promise<boolean>((resolve) => {
      let pc: RTCPeerConnection;
      try {
        pc = new RTCPeerConnection({ iceServers: stunServers() });
      } catch {
        resolve(false);
        return;
      }

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        try {
          pc.close();
        } catch {
          /* ignore */
        }
        resolve(true);
      };

      pc.onicecandidate = (e) => {
        if (!e.candidate) {
          finish();
          return;
        }
        const c = e.candidate;
        candidates.push({
          type: c.type ?? '',
          protocol: (c.protocol ?? '').toLowerCase(),
          address: c.address ?? '',
          port: c.port ?? 0,
          priority: c.priority ?? 0,
          isMdns: /\.local$/i.test(c.address ?? ''),
        });
      };
      // Some environments never fire onicecandidate; cap with a timer.
      setTimeout(finish, 3000);

      // Trigger gathering by adding a transceivers / datachannel.
      try {
        pc.createDataChannel('probe');
        pc.createOffer()
          .then((offer) => pc.setLocalDescription(offer))
          .catch(() => finish());
      } catch {
        finish();
      }
    });

    if (!gathered || candidates.length === 0) {
      return {
        signals: {
          supported: ok(true, 'client'),
          candidateCount: ok(0, 'client'),
          note: ok('no ICE candidates gathered (likely no network interfaces, or WebRTC disabled)', 'client'),
        },
      };
    }

    // --- analysis ---------------------------------------------------------
    const types = candidates.map((c) => c.type);
    const hasHost = types.includes('host');
    const hasSrflx = types.includes('srflx');
    const hasRelay = types.includes('relay');
    const hasMdns = candidates.some((c) => c.isMdns);
    const protocols = [...new Set(candidates.map((c) => c.protocol))].sort();

    const claimedChromium = /Chrome|Chromium|Edg\//.test(navigator.userAgent);
    // Chrome obfuscates host candidates with mDNS by default. If a browser
    // claims Chromium but exposes raw host IPs (no .local), the mDNS layer was
    // either disabled or the stack is not really libwebrtc.
    if (claimedChromium && hasHost && !hasMdns) {
      findings.push({
        id: 'webrtc.chromium-without-mdns',
        severity: 'medium',
        category: 'hardware',
        layer: 'webrtc',
        confidence: 'medium',
        title: 'Chromium User-Agent but no mDNS-obfuscated ICE candidates',
        detail:
          'Chrome and other Chromium browsers replace host candidate IPs with generated *.local mDNS names by default for privacy. A client claiming to be Chromium that exposes raw host addresses is either not real Chromium, or has had its WebRTC stack altered. Non-browser ICE stacks (pion/ice, aioice, str0m) also lack the mDNS layer, so this is meaningful but not conclusive on its own.',
        sources: [WEBRTC_SOURCE, ICE_SOURCE, 'https://issues.chromium.org/issues/471788748'],
        evidence: [`host=${hasHost}`, `mdns=${hasMdns}`, `candidates=${candidates.length}`],
        scoreContribution: 20,
      });
    }

    // Gathering order: libwebrtc gathers host, then srflx, then relay, in an
    // interleaved order. A strictly sequential or empty srflx/relay with many
    // host candidates is consistent with a minimal/non-browser stack, but is not
    // scored on its own.
    const gatheringShape = summarizeOrder(types);

    // --- reflexive address -------------------------------------------------
    // A public STUN server reports the source address it saw, so the srflx
    // candidates expose the page's own public IP without any server we operate.
    // Multiple servers SHOULD agree; disagreement means something in the path
    // is rewriting the source address, and absence means a fully-concealing
    // proxy (or UDP blocked outright).
    const srflxIps = [
      ...new Set(
        candidates
          .filter((c) => c.type === 'srflx' && c.address && !c.isMdns)
          .map((c) => c.address),
      ),
    ];
    const srflxUnique = srflxIps.length;
    // IP shape only. The address itself is transient (mobile/CGNAT churn) and
    // is deliberately NOT hashed into any fingerprint part.
    const srflxShape = describeAddressShape(srflxIps);
    const srflxAgrees = srflxUnique === 1;

    if (hasSrflx && srflxUnique > 1) {
      findings.push({
        id: 'webrtc.reflexive-ip-disagreement',
        severity: 'medium',
        category: 'network',
        layer: 'webrtc',
        confidence: 'medium',
        title: `Public STUN servers disagree on the reflexive address (${srflxUnique} distinct values)`,
        detail:
          'Several independent STUN servers each reported a different source address for the same page load. A direct connection produces one stable public IP regardless of which server observes it, so disagreement indicates an intercepting proxy or VPN that rewrites the source address inconsistently per destination. This can also appear with carrier-grade NAT pools under load, so it is a prompt to investigate rather than proof of a proxy.',
        sources: [STUN_SOURCE, ICE_SOURCE],
        evidence: srflxIps.map((ip, i) => `stun server ${i + 1}: ${ip}`),
        scoreContribution: 25,
      });
    }

    return {
      signals: {
        supported: ok(true, 'client'),
        candidateCount: ok(candidates.length, 'client'),
        hasHost: ok(hasHost, 'client'),
        hasSrflx: ok(hasSrflx, 'client'),
        hasRelay: ok(hasRelay, 'client'),
        hasMdns: ok(hasMdns, 'client'),
        protocols: ok(protocols, 'client'),
        gatheringShape: ok(gatheringShape, 'derived'),
        // Expose a few concrete candidates for the reader, but hashed into the
        // part as a shape (addresses are ephemeral and IP-like).
        sampleTypes: ok([...new Set(types)], 'client'),
        // --- reflexive address summary ---
        // Counts and shape, never the literal address: the IP is volatile and
        // identifying, and the reader can see it in the expanded raw signals.
        srflxCount: ok(srflxIps.length, 'derived'),
        srflxDistinctValues: ok(srflxUnique, 'derived'),
        srflxAgreesAcrossServers: ok(srflxAgrees, 'derived'),
        srflxAddressShape: ok(srflxShape, 'derived'),
        publicStunConfigured: ok(true, 'derived'),
        // The literal addresses, for the egress cross-check in the DoH probe.
        // Deliberately NOT hashed into any part: these are the visitor's public
        // IPs and they change whenever the network does.
        srflxIps: ok(srflxIps, 'client'),
      },
      findings,
      parts: {
        webRtc: {
          hasHost,
          hasSrflx,
          hasRelay,
          hasMdns,
          protocols,
          gatheringShape,
          // Shape only, so a mobile IP change does not change the fingerprint.
          srflxShape,
          srflxAgrees,
        },
        // The ICE part emphasizes the mDNS + gathering shape.
        ice: {
          hasMdns,
          gatheringShape,
          protocols,
          srflxAgrees,
        },
      },
    };
  },
};

/**
 * Classify a public IP without recording it: a coarse address-family and
 * allocation hint that is stable for a given network but not identifying.
 */
function describeAddressShape(ips: string[]): string {
  if (ips.length === 0) return 'none';
  const kinds = new Set<string>();
  for (const ip of ips) {
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(ip)) {
      // RFC1918 / loopback / link-local would indicate a broken NAT, not a
      // public address, so they are worth distinguishing.
      if (/^10\./.test(ip) || /^192\.168\./.test(ip) || /^172\.(1[6-9]|2\d|3[01])\./.test(ip)) {
        kinds.add('rfc1918');
      } else if (/^127\./.test(ip)) kinds.add('loopback');
      else if (/^169\.254\./.test(ip)) kinds.add('link-local');
      else if (/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(ip)) kinds.add('cgnat');
      else kinds.add('public-v4');
    } else if (ip.includes(':')) {
      kinds.add('public-v6');
    } else {
      kinds.add('other');
    }
  }
  return [...kinds].sort().join('+');
}

/**
 * Public STUN servers, used by default.
 *
 * A STUN server answers a Binding Request with the source address the packet
 * arrived from (XOR-MAPPED-ADDRESS). That makes a serverless page able to read
 * its own public IP: the reflexive candidate it produces IS the answer, no
 * cooperation from the visitor's network required.
 *
 * These are well-known, free, public, and operated by large providers
 * precisely so anyone can use them. We use several rather than one so the
 * reflexive address can be CROSS-CHECKED: a genuine network path produces the
 * same public IP at every STUN server, whereas a transparent proxy that
 * rewrites the source address inconsistently produces different values, and a
 * fully-concealing proxy produces none at all.
 *
 * Overridable with VITE_STUN_URL for operators who prefer their own.
 */
const PUBLIC_STUN: string[] = [
  'stun:stun.l.google.com:19302',
  'stun:stun1.l.google.com:19302',
  'stun:stun.cloudflare.com:3478',
];

const STUN_SOURCE = 'https://datatracker.ietf.org/doc/html/rfc8489';

function stunServers(): RTCIceServer[] {
  const url = import.meta.env?.VITE_STUN_URL as string | undefined;
  if (url) return [{ urls: url }];
  // No STUN configured: fall back to the public defaults so the reflexive
  // candidate (and therefore the public IP) is still observable.
  return PUBLIC_STUN.map((urls) => ({ urls }));
}

/** Compress the candidate type sequence into a shape signature. */
function summarizeOrder(types: string[]): string {
  // Collapse consecutive duplicates: e.g. host,host,srflx -> host,srflx
  const collapsed: string[] = [];
  for (const t of types) {
    if (collapsed[collapsed.length - 1] !== t) collapsed.push(t);
  }
  return collapsed.join('>');
}

registerProbe(webrtcProbe);
